import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
} from '@mui/material';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import {
    DicomTag,
    extractDicomTags,
    flattenAllTags,
    flattenVisibleTags,
} from '~/utils/dicomTags';

interface DicomTagDialogProps {
    open: boolean;
    onClose: () => void;
    dataset: DcmJsDatasetWrapper | null;
    title: string;
}

// DICOM tags containing patient-identifying information.
// Group 0010 (Patient) is always PHI.
const PHI_TAGS = new Set([
    '(0008,0020)', // StudyDate
    '(0008,0021)', // SeriesDate
    '(0008,0050)', // AccessionNumber
    '(0008,0090)', // ReferringPhysicianName
    '(0008,1030)', // StudyDescription
    '(0008,103E)', // SeriesDescription
]);

function isPhiTag(tag: string): boolean {
    if (tag.startsWith('(0010,')) {
        return true;
    }
    return PHI_TAGS.has(tag);
}

const monoSx = { fontFamily: 'monospace', fontSize: 12 } as const;

const headerCellSx = {
    fontSize: 12,
    position: 'sticky' as const,
    bgcolor: 'background.paper',
    borderColor: 'divider',
    zIndex: 1,
} as const;

const cellSx = {
    py: 0.5,
    fontSize: 12,
    borderColor: 'divider',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as const;

function filterTags(tags: DicomTag[], query: string): DicomTag[] {
    if (!query) {
        return tags;
    }
    const lower = query.toLowerCase();
    return tags.filter(
        (t) =>
            t.tag.toLowerCase().includes(lower) ||
            t.name.toLowerCase().includes(lower) ||
            t.value.toLowerCase().includes(lower)
    );
}

const DEPTH_INDENT_PX = 24;

function TagToggle({
    tag,
    isExpanded,
    onToggle,
    toggleLabel,
}: {
    tag: DicomTag;
    isExpanded: boolean;
    onToggle: (id: string) => void;
    toggleLabel: string;
}) {
    if (!tag.children) {
        return null;
    }

    const Icon = isExpanded ? ChevronDown : ChevronRight;
    return (
        <IconButton
            type="button"
            size="small"
            aria-expanded={isExpanded}
            aria-label={toggleLabel}
            onClick={(e) => {
                e.stopPropagation();
                onToggle(tag.id);
            }}
            sx={{
                p: 0,
                width: 22,
                height: 22,
                ml: '-4px',
                flexShrink: 0,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
            }}
        >
            <Icon size={14} />
        </IconButton>
    );
}

export default function DicomTagDialog({
    open,
    onClose,
    dataset,
    title,
}: DicomTagDialogProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const allTags = useMemo(
        () => (dataset ? extractDicomTags(dataset) : []),
        [dataset]
    );

    const flattenedAllTags = useMemo(() => flattenAllTags(allTags), [allTags]);

    const totalFlatCount = flattenedAllTags.length;

    const isSearching = search.length > 0;

    const visibleTags = useMemo(() => {
        if (isSearching) {
            return filterTags(flattenedAllTags, search);
        }
        return flattenVisibleTags(allTags, expanded);
    }, [flattenedAllTags, allTags, search, expanded, isSearching]);

    const handleToggle = useCallback((id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleClose = () => {
        setSearch('');
        setExpanded(new Set());
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ elevation: 0, sx: { height: '80vh' } }}
        >
            <DialogTitle sx={{ pb: 0 }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                >
                    <Typography variant="h6" component="span" noWrap>
                        <FormattedMessage id="dicom.tagViewer.title" /> -{' '}
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        flexShrink={0}
                    >
                        {visibleTags.length} / {totalFlatCount}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{ display: 'flex', flexDirection: 'column', px: 2, pb: 0 }}
            >
                <Box py={1} px={1}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={intl.formatMessage({
                            id: 'dicom.tagViewer.search',
                        })}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={16} />
                                </InputAdornment>
                            ),
                        }}
                        autoFocus
                    />
                </Box>

                <TableContainer
                    sx={{
                        flex: 1,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        px: 1,
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...headerCellSx, width: 160 }}>
                                    <FormattedMessage id="dicom.tagViewer.tag" />
                                </TableCell>
                                <TableCell sx={{ ...headerCellSx, width: 50 }}>
                                    <FormattedMessage id="dicom.tagViewer.vr" />
                                </TableCell>
                                <TableCell sx={{ ...headerCellSx, width: 220 }}>
                                    <FormattedMessage id="dicom.tagViewer.name" />
                                </TableCell>
                                <TableCell sx={headerCellSx}>
                                    <FormattedMessage id="dicom.tagViewer.value" />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleTags.map((t) => {
                                const isSequence = !!t.children;
                                const isSeparator =
                                    !t.children && !t.tag && !t.vr;
                                const indent = isSearching
                                    ? 0
                                    : t.depth * DEPTH_INDENT_PX;

                                if (isSeparator) {
                                    return (
                                        <TableRow key={t.id}>
                                            <TableCell
                                                colSpan={4}
                                                sx={{
                                                    py: 0,
                                                    px: 0,
                                                    pl: `${indent}px`,
                                                    border: 'none',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        pt: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            color: 'text.disabled',
                                                            textTransform:
                                                                'uppercase',
                                                            letterSpacing: 0.5,
                                                            whiteSpace:
                                                                'nowrap',
                                                            pl: 1,
                                                        }}
                                                    >
                                                        {t.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }

                                return (
                                    <TableRow
                                        key={t.id}
                                        hover
                                        onClick={
                                            isSequence
                                                ? () => handleToggle(t.id)
                                                : undefined
                                        }
                                        sx={{
                                            ...(isSequence && {
                                                cursor: 'pointer',
                                            }),
                                            ...(indent > 0 && {
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: `${indent}px`,
                                                    right: 0,
                                                    height: '1px',
                                                    bgcolor: 'divider',
                                                },
                                            }),
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                ...cellSx,
                                                ...monoSx,
                                                pl: `${8 + indent}px`,
                                                ...(indent > 0 && {
                                                    border: 'none',
                                                }),
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                {!isSearching && (
                                                    <TagToggle
                                                        tag={t}
                                                        isExpanded={expanded.has(
                                                            t.id
                                                        )}
                                                        onToggle={handleToggle}
                                                        toggleLabel={intl.formatMessage(
                                                            {
                                                                id: expanded.has(
                                                                    t.id
                                                                )
                                                                    ? 'dicom.tagViewer.sequenceCollapse'
                                                                    : 'dicom.tagViewer.sequenceExpand',
                                                            }
                                                        )}
                                                    />
                                                )}
                                                {t.tag}
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...cellSx,
                                                ...monoSx,
                                                ...(indent > 0 && {
                                                    border: 'none',
                                                }),
                                            }}
                                        >
                                            {t.vr}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...cellSx,
                                                fontWeight: isSequence
                                                    ? 500
                                                    : undefined,
                                                ...(indent > 0 && {
                                                    border: 'none',
                                                }),
                                            }}
                                        >
                                            {t.name}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...cellSx,
                                                ...monoSx,
                                                maxWidth: 0,
                                                ...(indent > 0 && {
                                                    border: 'none',
                                                }),
                                                ...(!showSensitiveInfo &&
                                                    isPhiTag(t.tag) && {
                                                        filter: 'blur(0.5rem)',
                                                        userSelect: 'none',
                                                    }),
                                            }}
                                            title={
                                                !showSensitiveInfo &&
                                                isPhiTag(t.tag)
                                                    ? undefined
                                                    : t.value
                                            }
                                        >
                                            {t.value}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {visibleTags.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        align="center"
                                        sx={{ py: 4 }}
                                    >
                                        <Typography
                                            color="text.secondary"
                                            fontSize={13}
                                        >
                                            {search
                                                ? intl.formatMessage(
                                                      {
                                                          id: 'dicom.tagViewer.noResults',
                                                      },
                                                      { query: search }
                                                  )
                                                : intl.formatMessage({
                                                      id: 'dicom.tagViewer.empty',
                                                  })}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    <FormattedMessage id="close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
